<template>
  <div role="search">
    <!-- Searching for a location -->
    <div class="t-flex t-flex-col lg:t-flex-row lg:t-justify-between">
      <label class="t-flex t-text-lg t-font-semibold t-text-gray-800 t-m-0">
        Search
      </label>
      <span class="t-flex t-text-sm t-text-gray-600 t-items-baseline lg:t-items-center">
        <img
          src="/images/favicon.png"
          class="t-mr-1 t-h-4 t-w-4"
          alt="Freighthub Logo"
        />
        indicates locations that are already part of our 15,000+ strong customer
        network
      </span>
    </div>

    <!-- Search component and results -->
    <locations-autocomplete
      ref="autocomplete"
      :source="search.results"
      :searchOnSourceChange="true"
      :show-no-results="search.showResults"
      :loading-prop="search.isLoading"
      @input="onSearchInputChange"
      @selected="onSearchResultSelected"
      placeholder="Enter a business name or a physical street address"
      results-display="textDisplay"
      name="name"
    >
      <template #icon="{ result }">
        <i
          v-if="result.source === SEARCH_SOURCES.SEARCH_SOURCE_FH"
          class="t-h-5 t-w-5 t-pt-1 t-inline-block"
        >
          <img
            src="/images/favicon.png"
            alt="Freighthub Logo"
            class="t-h-5 t-w-5"
          />
        </i>
        <i
          v-else-if="result.source === SEARCH_SOURCES.SEARCH_SOURCE_GOOGLE"
          class="fas fa-map-marker t-h-5 t-w-5"
        />
      </template>

      <template #subtext="{ result }">
        <p class="t-mb-0 t-ml-6 t-text-sm">
          {{ result.subtextDisplay }}
        </p>
      </template>

      <template #noResultsText>
        <p>
          Searching for a location. If none appear, please try entering part or
          all of a business name, or an address.
        </p>
      </template>
    </locations-autocomplete>

    <div class="t-mt-1 t-flex-col md:t-flex-row t-justify-between">
      <span class="t-inline-block t-float-right">
        <img
          src="/images/powered_by_google_on_white.png"
          alt="Powered By Google"
          class="t-float-right t-w-3/4"
        />
      </span>
      <!--      <button
        v-if="!errorMessage"
        @click="onEnterManualLocationClick"
        class="t-text-sm t-font-medium t-text-blue-700 t-underline t-mt-1 focus:t-outline-none hover:t-text-blue-500"
      >
        Enter a location manually
      </button>-->
    </div>

    <div
      v-if="errorMessage"
      class="t-font-medium t-text-sm t-text-red-600 t-my-2"
    >
      {{ errorMessage }}
    </div>

    <!-- Entering a location manually -->
    <enter-manual-location-form
      v-if="
        isManuallyEnteringLocation ||
        (missingLocationFields && missingLocationFields.length > 0)
      "
      :location-data="location"
      :required-location-fields="missingLocationFields"
      @enterManualLocationCancelled="onEnterManualLocationCancelled"
      @enterManualLocationConfirmed="onEnterManualLocationConfirmed"
    />

    <!--
    After search, if the location is not found on Freighthub, ask if it is associated with their business.
    This will affect what form fields they can fill up.
    -->
    <div v-if="search.locationSelected" class="t-mt-4">
      <hr />
      <p class="t-text-lg t-font-medium t-mt-4 t-mb-6">
        Is this new location owned by or part of your company?
      </p>

      <div
        class="t-grid t-grid-rows-2 lg:t-grid-rows-1 t-grid-cols-1 lg:t-grid-cols-2 t-gap-24 lg:t-px-20 t-mb-8"
      >
        <button
          @click.prevent="onChooseLocationRelationShip(location, false)"
          class="t-mx-auto t-flex-col t-border-2 t-border-gray-100 t-shadow-lg t-p-6 t-rounded-lg focus:t-outline-none hover:t-border-4 hover:t-border-blue-400"
        >
          <span
            class="t-block t-mb-4 t-border-b-2 t-border-gray-300 t-pb-4 t-font-bold t-text-xl t-text-center"
          >
            No
          </span>
          <span class="t-text-lg t-font-light">
            This new location is not owned by or part of my company
          </span>
        </button>

        <button
          @click.prevent="onChooseLocationRelationShip(location, true)"
          class="t-mx-auto t-flex-col t-border-2 t-border-gray-100 t-shadow-lg t-p-6 t-rounded-lg focus:t-outline-none hover:t-border-4 hover:t-border-blue-400"
        >
          <span
            class="t-block t-mb-4 t-border-b-2 t-border-gray-300 t-pb-4 t-text-xl t-font-bold t-text-center"
          >
            Yes
          </span>
          <span class="t-text-lg t-font-light">
            This new location is owned by or part of my company
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LocationsAutocomplete from "./LocationsAutocomplete";
import EnterManualLocationForm from "../EnterManualLocationForm";

export default {
  name: 'SearchLocationForm',
  description: '',
  components: {
    LocationsAutocomplete,
    EnterManualLocationForm,
  },
  emits: ['selectedLocation', 'changeFormType'],
  data() {
    return {
      errorMessage: '',
      isManuallyEnteringLocation: false,
      missingLocationFields: [],
      isValidLocation: false,
      search: {
        results: [],
        showResults: false,
        autocompleteService: null,
        placesService: null,
        locationSelected: false,
        isLoading: false,
      },
      location: {
        name: '',
        address: {
          street_number: '',
          street_name: '',
          suburb: '',
          city: '',
        },
        sourcedFrom: null
      },
    };
  },
  created() {
    this.SEARCH_SOURCES = {
      SEARCH_SOURCE_FH: 'SEARCH_SOURCE_FH',
      SEARCH_SOURCE_GOOGLE: 'SEARCH_SOURCE_GOOGLE',
    };
  },
  methods: {
    onSearchInputChange: _.debounce(async function (event, searchText) {
      try {
        if (!searchText) {
          return;
        }
        if (this.search.locationSelected) {
          return;
        }

        this.search.showResults = false;
        this.search.isLoading = true;
        let newSearchResults = [];
        let searchResultMap = new Map();

        // Search for existing locations on Freighthub
        let fhRes = await axios.get(`/api/userlocations?q=${searchText}&g=global`);
        let fhResData = await fhRes.data;

        for (let key in fhResData) {
          if (fhResData.hasOwnProperty(key)) {
            let fhLocation = fhResData[key];
            newSearchResults.push({
              textDisplay: fhLocation['name'],
              subtextDisplay: `
                  ${fhLocation['address']['street_number']} ${fhLocation['address']['street_name']},
                  ${fhLocation['address']['suburb']}, ${fhLocation['address']['city']}
                `,
              name: fhLocation['name'],
              address: fhLocation['address'],
              id: fhLocation['value'],
              source: this.SEARCH_SOURCES.SEARCH_SOURCE_FH,
              already_added_location: fhLocation['already_added_location']
            });
            searchResultMap.set(fhLocation['google_place_id'], fhLocation);
          }
        }

        // Also use Google Places to search for locations
        if (!this.search.autoCompleteService) {
          this.search.autoCompleteService = new google.maps.places.AutocompleteService();
        }
        this.search.autoCompleteService.getPlacePredictions(
          {
            input: searchText,
            componentRestrictions: {
              country: 'nz',
            },
          },
          async (placesResData) => {
            // When suggestions from Google Places are received, check if the location already exists on Freighthub using its place_id
            for (let key in placesResData) {
              if (placesResData.hasOwnProperty(key)) {
                let placeId = placesResData[key]['place_id'];
                let res = await axios.get(`/settings/locations/place-id?place_id=${placeId}`);
                let location = res.data;

                if (searchResultMap.has(placeId)) {
                  continue;
                }

                if (location.exists) {
                  // If a location exists, push the Freighthub location to the user's search results instead of Google's one
                  newSearchResults.push({
                    textDisplay: location['name'],
                    subtextDisplay: `
                      ${location['address']['street_number']} ${location['address']['street_name']},
                      ${location['address']['suburb']}, ${location['address']['city']}
                    `,
                    name: location['name'],
                    address: location['address'],
                    id: location['value'],
                    source: this.SEARCH_SOURCES.SEARCH_SOURCE_FH,
                  });
                } else {
                  // If a location does not exist within Freighthub, use the Google Places Suggestion
                  if (!this.search.placesService) {
                    this.search.placesService = new google.maps.places.PlacesService(
                      document.createElement('div')
                    );
                  }

                  // Looks for a full formatted address of a location to show to the user
                  this.search.placesService.getDetails(
                    {
                      placeId: placeId,
                      fields: ['formatted_address'],
                    },
                    (placeDetails) => {
                      let placeData = placesResData[key]['structured_formatting'];
                      newSearchResults.push({
                        textDisplay: placeData['main_text'],
                        subtextDisplay: placeDetails && placeDetails.formatted_address ? placeDetails.formatted_address : placeData['secondary_text'],
                        name: placeData['main_text'],
                        address: placeDetails && placeDetails.formatted_address ? placeDetails.formatted_address : placeData['secondary_text'],
                        placeId: placeId,
                        source: this.SEARCH_SOURCES.SEARCH_SOURCE_GOOGLE,
                      });
                    }
                  );
                }
              }
            }
            this.search.isLoading = false;
          }
        );

        this.search.results = newSearchResults;
      } catch (error) {
        console.error(error);
      }
      this.search.showResults = true;
    }, 500),

    /**
     * When a location is selected, this method will get more details about the location based on where
     * it was sourced from. If it was sourced from Google Places, it will use the Place Details API
     * to get more information. This will allow the user to have fewer form fields to fill.
     * @param searchResult
     */
    onSearchResultSelected(searchResult) {

      if (searchResult.selectedObject.source === this.SEARCH_SOURCES.SEARCH_SOURCE_FH) {
        this.location = searchResult.selectedObject;
        this.location.id = searchResult.selectedObject.id;
        this.location.name = searchResult.selectedObject.name;
        this.isValidLocation = true;

        // Disable searching/manually entering
        this.isSearchingLocation = false;
        this.isManuallyEnteringLocation = false;

        this.$emit('changeFormType', 'AddFreighthubLocationForm', this.location);
      } else if (searchResult.selectedObject.source === this.SEARCH_SOURCES.SEARCH_SOURCE_GOOGLE) {
        // If the result was found through Google Place Autocomplete
        this.location = searchResult.selectedObject;
        this.location.sourcedFrom = this.SEARCH_SOURCES.SEARCH_SOURCE_GOOGLE;

        // Get place information based on its Google Place ID. First check if placesService is initialised.
        if (!this.search.placesService) {
          this.search.placesService = new google.maps.places.PlacesService(document.createElement('div'));
        }

        // Search for the place's details
        this.search.placesService.getDetails(
          {
            placeId: searchResult.selectedObject.placeId,
            fields: [
              'name',
              'address_component',
              'geometry',
              'formatted_address',
              'opening_hours',
            ],
          },
          async (placeDetails) => {
            try {
              this.location.name = placeDetails.name;
              this.location.sourcedFrom = 'GOOGLE_PLACES';

              let address = {
                street_number: '',
                street_name: '',
                suburb: '',
                city: '',
                post_code: '',
                formattedAddress: '',
                placeId: placeDetails.placeId,
                latitude: '',
                longitude: '',
              };

              // Retrieve address information. Go through address types and address fields based on Google Places' categories
              for (let addressComponent of placeDetails.address_components) {
                let component = addressComponent.long_name;
                for (let addressType of addressComponent.types) {
                  if (addressType === 'floor' || addressType === 'room' || addressType === 'subpremise') {
                    address.street_number = component + '/';
                  } else if (addressType === 'street_number') {
                    address.street_number += component;
                  } else if (addressType === 'street_address' || addressType === 'route') {
                    address.street_name = component;
                  } else if (addressType === 'sublocality') {
                    address.suburb = component;
                  } else if (addressType === 'locality') {
                    address.city = component;
                  } else if (addressType === 'postal_code') {
                    address.post_code = component;
                  }
                }
              }

              // If the address doesn't have a suburb (e.g. small city), fill out the suburb field using the city name
              if (address.suburb === '') {
                let data = {
                  suburb: address.city,
                  lat: placeDetails.geometry.location.lat(),
                  lng: placeDetails.geometry.location.lng()
                }

                let config = {
                  headers: {
                    'Content-Type': 'application/json'
                  }
                };
                let res = await axios.post(`/settings/locations/suburb`, data, config);
                address.suburb = res.data.suburb;
              }

              address.formattedAddress = placeDetails.formatted_address ?
                placeDetails.formatted_address :
                `${address.street_number} ${address.street_name}, ${address.suburb}, ${address.city}`;
              address.placeId = searchResult.selectedObject.placeId;
              address.latitude = placeDetails.geometry.location.lat();
              address.longitude = placeDetails.geometry.location.lng();

              this.location.address = {
                data: address,
              };

              // Retrieve business hours information
              if (placeDetails.opening_hours) {
                let openingHours = placeDetails.opening_hours.periods.map(
                  (openingPeriod) => {
                    return {
                      id: openingPeriod.open.day,
                      weekday_id: openingPeriod.open.day,
                      opening_hour: moment(openingPeriod.open.time, 'HHmm').format('HH:mm'),
                      closing_hour: openingPeriod.close
                        ? moment(openingPeriod.close.time, 'HHmm').format('HH:mm')
                        : '17:30',
                    };
                  }
                );

                this.location.openingHours = {
                  data: openingHours,
                };
              }

              this.search.locationSelected = true;
            } catch (e) {
              console.exception(e);
            }
          }
        );
      } else {
        console.exception('Unknown location source given');
      }
    },

    onChooseLocationRelationShip(location, ownershipStatus) {
      if (ownershipStatus) {
        ownershipStatus = 'OWNED_BY_YOU';
      } else {
        ownershipStatus = 'OWNED_BY_NO_ONE';
      }

      location = {
        ...location,
        ownershipStatus,
      };

      if (ownershipStatus === 'OWNED_BY_YOU') {
        this.$emit('changeFormHeader', 'OwnsNewLocation');
      } else if (ownershipStatus === 'OWNED_BY_ANOTHER') {
        this.$emit('changeFormHeader', 'NotOwnsNewLocation');
      }
      this.$emit('changeFormType', 'SaveLocationForm', location);
    },
  },
};
</script>
